import { Box, Button, styled } from '@mui/material';
import Modal from '@mui/material/Modal';
import { PropsWithChildren } from 'react';
import { AUTH_MODAL_Z_INDEX } from 'style/constants';

const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '658px',
  height: 'var(--auth-modal-container-height)',
  background: `linear-gradient( rgba(1, 8, 10, 0.96), rgba(1, 8, 10, 0.96)) padding-box,
              linear-gradient(156.52deg, rgba(115, 246, 249, 0.4) 2.12%, rgba(115, 246, 249, 0.0001) 39%, rgba(115, 246, 249, 0.0001) 54.33%, rgba(115, 246, 249, 0.1) 93.02%) border-box`,
  color: theme.palette.text.secondary,
  padding: theme.spacing(7, 14, 7, 9),
  borderRadius: '20px',
  border: `2px solid transparent`,
  gridTemplateAreas: `"logo logo tabs"
                      "stepper content content"
                      ". buttons buttons"
                      ". links links"`,
  display: 'grid',
  gridTemplateRows: '70px auto 30px 30px',
  gridTemplateColumns: '50px 3fr 2fr',
  gap: theme.spacing(7),
}));

const Logo = styled('div')(({ theme }) => ({
  gridArea: 'logo',
  marginLeft: theme.spacing(4),
  '> img': {
    height: '100%',
  },
}));

const Backdrop = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '558px',
  height: '488px',
  zIndex: -1,
  opacity: 0.5,
  background: '#D9D9D9',
  boxShadow: '0px 0px 200px 60px #35E2E6;',
  borderRadius: '1000px',
});

const Tabs = styled('div')({
  gridArea: 'tabs',
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'end',
});

const Content = styled('div')(({ theme }) => ({
  gridArea: 'content',
  overflowY: 'auto',
  marginTop: theme.spacing(6),
  overflowX: 'hidden',
}));

const Buttons = styled('div')({
  button: {
    margin: '0 !important',
  },
  gridArea: 'buttons',
  display: 'flex',
  justifyContent: 'center',
  '&:has(> button + button)': {
    justifyContent: 'space-between',
  },
});

const Stepper = styled('div')(({ theme }) => ({
  gridArea: 'stepper',
  maxWidth: '50px',
  marginLeft: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
}));

const Links = styled('div')({
  gridArea: 'links',
  display: 'flex',
  justifyContent: 'center',
});

const Title = styled('h3')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: theme.typography.h3.fontSize,
  fontWeight: '400',
}));

const Link = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: theme.typography.textBase.fontSize,
  color: theme.palette.text.secondary,
  boxShadow: 'none',

  ':hover': {
    textDecoration: 'underline',
  },
}));

interface AuthModalProps extends PropsWithChildren {
  open: boolean;
}

const AuthModal = ({ children, open }: AuthModalProps) => {
  return (
    <Modal
      open={open}
      disableAutoFocus
      slots={{ backdrop: Backdrop }}
      sx={{ zIndex: AUTH_MODAL_Z_INDEX }}
    >
      <ModalContainer>{children}</ModalContainer>
    </Modal>
  );
};

AuthModal.Logo = Logo;
AuthModal.Tabs = Tabs;
AuthModal.Content = Content;
AuthModal.Buttons = Buttons;
AuthModal.Links = Links;
AuthModal.Stepper = Stepper;
AuthModal.Title = Title;
AuthModal.Link = Link;

export default AuthModal;
